import { useLingui } from "@lingui/react/macro";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { deducedError, useErrorMessageAlert } from "@/Utils/ErrorUtils";
import { FilledButton, OutlinedButton } from "@medoma/components/Button/Button";
import { useForm, FormProvider } from "react-hook-form";
import styles from "../Pause/Pause.module.scss";
import type { IExistingPatient } from "@medoma/models/patients";
import {
  type IActivity,
  patientActivitiesQueryOptions,
} from "@/api/Activities";
import { reactivateClosedActivities } from "@/api/Activities";
import { Loading } from "@medoma/components/Loading/Loading";
import { unpausePatient, useParentRoutesPatient } from "@/api/Patients";
import { useNavigate } from "react-router";
import Form from "@/components/Form/Form";
import { Text } from "@medoma/components/Text/Text";
import { Trans } from "@lingui/react/macro";
import { ActivitiesToCloseOrReactivate } from "@/pages/commandcenter/Patients/Patient/CareEpisode/ActivitiesToCloseOrReactivate";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { CareEpisodeSubpage } from "../CareEpisodeSubpage";
import subpageStyles from "../CareEpisodeSubpage.module.scss";

export const UnpauseSection = () => {
  const { t } = useLingui();
  const patient = useParentRoutesPatient();

  return (
    <CareEpisodeSubpage title={t`Återuppta`}>
      <div className={subpageStyles.subPageContainer}>
        <Unpause patient={patient} />
      </div>
    </CareEpisodeSubpage>
  );
};

type IUnpauseFormData = {
  activityIds: string[];
};

const Unpause = ({ patient }: { patient: IExistingPatient }) => {
  const {
    data: closedActivities,
    isPending,
    isError,
  } = useQuery(
    patientActivitiesQueryOptions({
      patientId: patient.id,
      closeReason: "pause",
    }),
  );

  const { t } = useLingui();

  if (isPending) {
    return <Loading message={t`Hämtar patientens tidigare aktiviteter`} />;
  }

  return (
    <UnpauseForm
      patient={patient}
      closedActivities={closedActivities ?? []}
      errorFetchingActivities={isError}
    />
  );
};

const UnpauseForm = ({
  patient,
  closedActivities,
  errorFetchingActivities,
}: {
  patient: IExistingPatient;
  closedActivities: IActivity[];
  errorFetchingActivities: boolean;
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useLingui();
  const { displayErrorMessageAlert } = useErrorMessageAlert();

  const { mutate: reactivateActivitiesMutation, isPending: isReactivating } =
    useMutation({
      mutationFn: async ({
        patientId,
        activityIds,
      }: {
        patientId: string;
        activityIds: string[];
      }) => {
        await reactivateClosedActivities({
          patientId,
          closeReason: "pause",
          oldCareEpisodeId: null,
          activityIds,
        });
      },
      onError: (error) => {
        displayErrorMessageAlert(
          `${t`Det gick inte att återaktivera aktiviteterna.`} ${deducedError(error)}`,
        );
      },
      onSuccess: () => {
        void queryClient.invalidateQueries();
        void navigate("..", { relative: "path" });
      },
    });

  const { mutate: unpausePatientMutation, isPending: isUnpausing } =
    useMutation({
      mutationFn: async ({
        patientId,
        activitiesToReactivate,
      }: {
        patientId: string;
        activitiesToReactivate: string[];
      }) => {
        await unpausePatient(patientId);
        return { patientId, activitiesToReactivate };
      },
      onError: (error) => {
        displayErrorMessageAlert(
          `${t`Det gick inte att återuppta vårdperioden.`} ${deducedError(error)}`,
        );
      },
      onSuccess: async (data) => {
        // Trigger the second mutation after successful unpause
        if (data.activitiesToReactivate.length > 0) {
          reactivateActivitiesMutation({
            patientId: data.patientId,
            activityIds: data.activitiesToReactivate,
          });
        } else {
          await queryClient.invalidateQueries();
          void navigate("..", { relative: "path" });
        }
      },
    });

  const methods = useForm<IUnpauseFormData>({
    defaultValues: {
      activityIds: closedActivities.map(({ id }) => id),
    },
  });

  const { handleSubmit } = methods;

  const validateAndSubmit = handleSubmit((formData) => {
    unpausePatientMutation({
      patientId: patient.id,
      activitiesToReactivate: formData.activityIds,
    });
  });

  return (
    <div className={styles.pauseContent}>
      <FormProvider {...methods}>
        <Form onSubmit={validateAndSubmit}>
          {errorFetchingActivities ? (
            <ErrorMessage
              message={t`Kunde inte hämta information om avslutade aktiviteter. Vårdperioden kan fortfarande återupptas, men det kan finnas aktiviteter som inte kan återaktiveras.`}
            />
          ) : closedActivities.length > 0 ? (
            <ActivitiesToCloseOrReactivate
              activities={closedActivities}
              legend={t`Välj de aktiviteter som ska återaktiveras`}
            />
          ) : null}

          {closedActivities.length > 0 ? null : (
            <Text element="p">
              <Trans>Det finns inga aktiviteter att återaktivera.</Trans>
            </Text>
          )}

          <div className={styles.actions}>
            <OutlinedButton asLinkTo=".." relative="path">
              {t`Tillbaka`}
            </OutlinedButton>
            <FilledButton
              disabled={isUnpausing || isReactivating}
              type="submit"
            >
              {t`Återuppta`}
            </FilledButton>
          </div>
        </Form>
      </FormProvider>
    </div>
  );
};
